import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import PropTypes from 'prop-types';
import axios from 'axios';
import SoftTypography from 'components/SoftTypography';
import SoftBox from 'components/SoftBox';
import Grid from '@mui/material/Grid';
import FormField from 'layouts/applications/wizard/components/FormField';
import SoftDatePicker from 'components/SoftDatePicker';
import InfoIcon from '@mui/icons-material/Info';
import SoftButton from 'components/SoftButton';
import Swal from 'sweetalert2';

import ClientSelector from 'components/ClientSelector';
import ProviderSelector from 'components/ProviderSelector';
import IncotermSelector from 'components/IncotermSelector';
import TransportSelector from 'components/TransportSelector';
import ReferenciaSelector from 'components/RefSelector';

const ModalEdit = ({ show, handleClose, numeroOrdenCabecera, onRefresh }) => {
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [orderInfo, setOrderInfo] = useState(null);
  const [formData, setFormData] = useState({
    referencia: '',
    numeroOrden: '',
    origen: '',
    fechaEntrega: null,
    transporte: '',
    cliente: '',
    clienteId: '',   // <-- ID numérico del cliente
    proveedor: '',   // <-- ID numérico del proveedor
    fechaRealEntrega: null,
    incoterms: '',
  });

  // Parsear fechas entrantes a objeto Date
  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value.replace(/-/g, '/'));
    return isNaN(d.getTime()) ? null : d;
  };

  // Formatear Date a "YYYY-MM-DD"
  const formatDate = (date) => {
    if (!date) return null;
    return date.toISOString().slice(0, 10);
  };

  // 1) Cuando 'show' se vuelve true, traemos la info de la orden
  useEffect(() => {
    const fetchOrderInfo = async () => {
      setIsLoadingOrder(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/orden-info/`,
          { params: { id: numeroOrdenCabecera } }
        );
        const data = response.data[0];
        console.log('Datos de la orden:', data);
        setOrderInfo(data);
      } catch (error) {
        console.error('Error fetching order information:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los datos de la orden.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      } finally {
        setIsLoadingOrder(false);
      }
    };

    if (show) {
      fetchOrderInfo();
    }
    // Si 'show' cambia a false, podemos limpiar orderInfo (opcional)
    if (!show) {
      setOrderInfo(null);
      setFormData({
        referencia: '',
        numeroOrden: '',
        origen: '',
        fechaEntrega: null,
        transporte: '',
        cliente: '',
        clienteId: '',
        proveedor: '',
        fechaRealEntrega: null,
        incoterms: '',
      });
    }
  }, [show, numeroOrdenCabecera]);

  // Resuelve el id de tipo de transporte: prioriza el id que manda el backend;
  // si no viene (backend sin actualizar), cae al nombre normalizado (sin tildes).
  const resolveTransporteId = (info) => {
    if (info?.id_tipo_transporte != null && info.id_tipo_transporte !== '') {
      return info.id_tipo_transporte;
    }
    const nombre = (info?.transporte || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // quita tildes
      .toUpperCase()
      .trim();
    if (nombre === 'TERRESTRE') return 1;
    if (nombre === 'MARITIMO') return 2;
    if (nombre === 'AEREO') return 3;
    return '';
  };

  // 2) Cuando ya tenemos 'orderInfo', poblar formData
  useEffect(() => {
    if (!orderInfo) return;

    setFormData({
      referencia: orderInfo.referencia || '',
      numeroOrden: orderInfo.numero || '',
      origen: orderInfo.origen || '',
      fechaEntrega: orderInfo.entrega
        ? parseDate(orderInfo.entrega)
        : null,
      // id directo del backend, con fallback al nombre normalizado
      transporte: resolveTransporteId(orderInfo),
      cliente: orderInfo.cliente || '',
      clienteId: orderInfo.id_cliente || '',       // <-- aquí va el ID numérico
      proveedor: orderInfo.id_proveedor || '',     // <-- y aquí el ID numérico
      fechaRealEntrega: orderInfo.real_entrega
        ? parseDate(orderInfo.real_entrega)
        : null,
      incoterms: orderInfo.id_incoterm || '',
    });
  }, [orderInfo]);

  // 3) Manejo de cambios en campos de texto, fechas y selects personalizados
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (field, dateValue) => {
    const date = parseDate(dateValue);
    setFormData((prev) => ({ ...prev, [field]: date }));
  };

  // 4) Validación mínima antes de guardar
  const validateForm = () => {
    if (!formData.referencia.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'La referencia es obligatoria.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.numeroOrden.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'El número de orden es obligatorio.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.origen.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'El origen es obligatorio.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.fechaEntrega) {
      Swal.fire({
        title: 'Error',
        text: 'La fecha de entrega es obligatoria.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.transporte) {
      Swal.fire({
        title: 'Error',
        text: 'El transporte es obligatorio.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.clienteId) {
      Swal.fire({
        title: 'Error',
        text: 'Debe seleccionar un cliente.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.proveedor) {
      Swal.fire({
        title: 'Error',
        text: 'Debe seleccionar un proveedor.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.incoterms) {
      Swal.fire({
        title: 'Error',
        text: 'Debe seleccionar un incoterm.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    if (!formData.fechaRealEntrega) {
      Swal.fire({
        title: 'Error',
        text: 'La fecha real de entrega es obligatoria.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }
    return true;
  };

  // 5) Guardar cambios vía PATCH
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        reference: formData.referencia,
        order_number: formData.numeroOrden,
        client: formData.clienteId,     // normalmente el backend espera ID, no nombre
        provider: formData.proveedor,    // ID del proveedor
        origin: formData.origen,
        incoterms: formData.incoterms,
        id_tipo_transporte: formData.transporte,
        ETA: formData.fechaEntrega ? formatDate(formData.fechaEntrega) : null,
        fechareal_orden_cabecera: formData.fechaRealEntrega
          ? formatDate(formData.fechaRealEntrega)
          : null,
      };

      console.log('Payload a enviar:', payload);

      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/${numeroOrdenCabecera}/`,
        payload
      );

      Swal.fire({
        title: 'Orden Actualizada',
        text: 'La orden se ha actualizado exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });

      if (onRefresh) {
        console.log('Llamando a onRefresh desde ModalEdit');
        onRefresh();
      }
      handleClose();
    } catch (error) {
      console.error('Error saving order:', error);
      const errorMsg =
        error.response?.data?.error ||
        'Ocurrió un error al guardar los datos de la orden.';
      Swal.fire({
        title: 'Error',
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      backdropClassName="modal-backdrop"
      className="modal-container"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Editar Orden</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingOrder && (
          <SoftTypography variant="body2" color="info" mb={2}>
            Cargando datos de la orden...
          </SoftTypography>
        )}

        <div className="order-edit" style={{ marginBottom: '20px' }}>
          <SoftTypography variant="h5" fontWeight="bold" gutterBottom>
            <InfoIcon /> Datos Generales
          </SoftTypography>
          <SoftBox mt={2}>
            <Grid container spacing={3}>
              {/* Primera columna */}
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <ReferenciaSelector
                    selectedReferencia={formData.referencia}
                    setSelectedReferencia={(value) =>
                      setFormData((prev) => ({ ...prev, referencia: value }))
                    }
                  />
                </SoftBox>
                <SoftBox mb={2}>
                  <FormField
                    type="text"
                    label="# Número de orden"
                    name="numeroOrden"
                    value={formData.numeroOrden}
                    placeholder="#"
                    onChange={handleInputChange}
                  />
                </SoftBox>
                <SoftBox mb={2}>
                  <FormField
                    type="text"
                    label="Origen"
                    name="origen"
                    value={formData.origen}
                    placeholder="Origen"
                    onChange={handleInputChange}
                  />
                </SoftBox>
                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Fecha de entrega
                  </SoftTypography>
                  <SoftDatePicker
                    value={formData.fechaEntrega}
                    onChange={(date) => handleDateChange('fechaEntrega', date)}
                    options={{ dateFormat: 'd/m/Y' }}
                    input={{ placeholder: 'Seleccione' }}
                  />
                </SoftBox>
                <SoftBox mb={2}>
                  <TransportSelector
                    selectedTransport={formData.transporte}
                    setSelectedTransport={(value) =>
                      setFormData((prev) => ({ ...prev, transporte: value }))
                    }
                  />
                </SoftBox>
              </Grid>

              {/* Segunda columna */}
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <ClientSelector
                    selectedClient={formData.cliente}
                    setSelectedClient={(clientObj) => {
                      if (!clientObj || !clientObj.value) return;
                      setFormData((prev) => ({
                        ...prev,
                        cliente: clientObj.label,
                        clienteId: clientObj.value, // ID numérico
                        proveedor: '',             // reseteamos proveedor
                      }));
                    }}
                  />
                </SoftBox>

                {/* Sólo se renderiza ProviderSelector si ya existe un clienteId */}
                {formData.clienteId && (
                  <SoftBox mb={2}>
                    <ProviderSelector
                      key={formData.clienteId}            // <— forzamos remonte cuando cambie clientId
                      clientId={formData.clienteId}       // ID numérico del cliente
                      selectedProvider={formData.proveedor}
                      setSelectedProvider={(value) =>
                        setFormData((prev) => ({ ...prev, proveedor: value }))
                      }
                    />
                  </SoftBox>
                )}

                <SoftBox mb={2}>
                  <IncotermSelector
                    selectedIncoterm={formData.incoterms}
                    setSelectedIncoterm={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        incoterms: option?.value || '',
                      }))
                    }
                  />
                </SoftBox>
                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Fecha real de entrega
                  </SoftTypography>
                  <SoftDatePicker
                    value={formData.fechaRealEntrega}
                    onChange={(date) => handleDateChange('fechaRealEntrega', date)}
                    options={{ dateFormat: 'd/m/Y' }}
                    input={{ placeholder: 'Seleccione' }}
                  />
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton
          variant="gradient"
          color="success"
          onClick={handleSave}
          disabled={isSubmitting}
          sx={{ mr: 2 }}
        >
          {isSubmitting ? 'Procesando...' : 'Guardar Cambios'}
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="error"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cerrar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
};

ModalEdit.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  numeroOrdenCabecera: PropTypes.string.isRequired,
  onRefresh: PropTypes.func,
};

ModalEdit.defaultProps = {
  onRefresh: null,
};

export default ModalEdit;
